import '../payroll.css'

import { LoadingOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { usePayroll } from '@api/usePayroll'
import { PayPeriodDetails } from '@components/DriverManagement/PayPeriod/PayPeriodDetails'
import { ActionButtons } from '@components/DriverManagement/Payroll/PayrollReport/ActionButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { BBDTag, GraviButton, GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { message } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { gridViewManager } from '@/utils/grid'
import { dateFormat } from '@constants/dateFormat'
import { useUserSettings } from '@hooks/settings'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { PayrollRowExpand } from '../PayrollReportExpand/PayrollRowExpand'

const STORAGE_KEY = 'payroll-report'

export function PayrollReport() {
  const [selectedDriver, setSelectedDriver] = useState(null)
  const [filters, setFilters] = useState()
  const [weekCount, setWeekCount] = useState()

  const { usePayrollDatesQuery, usePayrollOverviewQuery, recalcSinglePayrollMutation } = usePayroll()
  const { data: payRollDates } = usePayrollDatesQuery()
  const { data: payrollOverviewData, isFetching } = usePayrollOverviewQuery(filters)
  const { mutateAsync: recalculatePayroll } = recalcSinglePayrollMutation()

  useEffect(() => {
    setFilters({ date: moment.utc(payRollDates?.start_date).format(dateFormat.DATE) })
    setWeekCount(payRollDates?.week_count)
  }, [payRollDates])

  const onWeekChange = (_date, dateString) => {
    setFilters({ date: moment.utc(dateString).format(dateFormat.DATE) })
  }
  const { updateSetting } = useUserSettings()
  const getRowId = useCallback((row) => row?.data?.id, [])
  const columnDefs = useMemo(() => getColumnDefs(recalculatePayroll, setSelectedDriver, filters?.date), [filters?.date])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )

  if (selectedDriver)
    return (
      <PayPeriodDetails
        driver={selectedDriver}
        setSelectedDriver={setSelectedDriver}
        weekCount={weekCount}
        onWeekChange={onWeekChange}
        controlFilters={filters}
      />
    )

  return (
    <GraviGrid
      loading={isFetching}
      rowData={payrollOverviewData?.rows}
      controlBarProps={{
        serverParams: payrollOverviewData?.params,
        filters,
        setFilters,
        hiddenFilterKeys: ['date'],
        title: 'Payroll Report',
        actionButtons: (
          <ActionButtons
            weekCount={weekCount}
            payRollDates={payRollDates}
            onWeekChange={onWeekChange}
            payrollRows={payrollOverviewData?.rows}
            filters={filters}
          />
        ),
      }}
      masterDetail
      detailCellRenderer={PayrollRowExpand}
      detailRowAutoHeight
      detailCellRendererParams={{
        startDate: filters?.date,
        setSelectedDriver,
      }}
      storageKey={STORAGE_KEY}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (recalculatePayroll, setSelectedDriver, selectedDate) => {
  return [
    {
      cellRenderer: 'agGroupCellRenderer',
      field: 'id',
      valueFormatter: () => '',
      headerName: '',
      maxWidth: 50,
    },
    {
      field: 'status',
      cellRenderer: ({ value, data }) => {
        return (
          <span className='payroll-status'>
            {data.has_error && (
              <BBDTag error>
                <b>Error</b>
              </BBDTag>
            )}
            {!data.has_error && value.toLowerCase() === 'approved' && (
              <BBDTag success textTransform='capitalize'>
                <b>{value}</b>
              </BBDTag>
            )}
            {!data.has_error && value.toLowerCase() === 'open' && (
              <BBDTag default textTransform='capitalize'>
                <b>{value}</b>
              </BBDTag>
            )}
          </span>
        )
      },
      maxWidth: 120,
    },
    {
      field: 'driver_name',
      headerName: 'Driver Name',
      cellRenderer: ({ value }) => <b>{value}</b>,
      comparator: (_, __, a, b) =>
        a?.data?.driver_name?.toLowerCase()?.localeCompare(b?.data?.driver_name?.toLowerCase()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      field: 'hours_worked',
      headerName: 'Hours Worked',
      align: 'right',
      ...defaultNumberDef('hours_worked'),
      cellRenderer: ({ value }) => <b>{value}</b>,
      comparator: (_, __, a, b) => a?.data?.hours_worked - b?.data?.hours_worked,
    },
    {
      field: 'pay_earned',
      headerName: 'Pay Earned',
      ...defaultNumberDef('pay_earned'),
      cellRenderer: ({ value, data }) => <PayEarnedValue value={value} data={data} />,
      comparator: (_, __, a, b) => a?.data?.pay_earned - b?.data?.pay_earned,
    },
    {
      sortable: false,
      filterValueGetter: (p) => p.data.payroll_configs?.map((x) => x.name).join(', '),
      headerName: 'Automatic Pay Config Names',
      field: 'payroll_configs',
      autoHeight: true,
      cellRenderer: ({ value }) =>
        !value?.length ? null : (
          <Vertical verticalCenter>
            {value.map((x) => (
              <Texto key={x.name}>{x.name}</Texto>
            ))}
          </Vertical>
        ),
    },
    {
      field: 'updated',
      valueGetter: ({ data }) => (data?.updated ? formatters.DATE_TIME(data.updated) : ''),
      maxWidth: 220,
    },
    {
      field: 'actions',
      minWidth: 400,
      cellRenderer: ({ data }) => {
        const { scopes } = useSecurityContext()
        const approved = data.status.toLowerCase() === 'approved'
        const canCalculatePayroll = scopes.driver?.automatic_pay || scopes.driver?.payroll_report.edit
        const [loading, setLoading] = useState(false)
        return (
          <Horizontal>
            {canCalculatePayroll && approved !== 'approved' && (
              <GraviButton
                onClick={() => {
                  setLoading(true)
                  recalculatePayroll({
                    driverId: data.driver_id,
                    date: moment.utc(selectedDate).startOf('day').toISOString(),
                  })
                    .then(() => message.success('Payroll Recalculated.'))
                    .finally(() => setTimeout(() => setLoading(false), 2000))
                }}
                loading={loading}
                size='small'
                className='mr-3'
                buttonText='Recalculate Payroll'
                icon={loading ? <LoadingOutlined /> : <ReloadOutlined className='pr-1 ml-1' />}
              />
            )}
            <GraviButton
              onClick={() => setSelectedDriver(data)}
              buttonText='View Period Details'
              size='small'
              theme2
              icon={<RightOutlined />}
            />
          </Horizontal>
        )
      },
    },
  ]
}

function PayEarnedValue({ value, data }) {
  const getPayPerHour = () => {
    if (data.hours_worked === 0) {
      return data.pay_earned
    }
    const payPerHr = data.pay_earned / data.hours_worked
    return payPerHr?.toFixed(2) || '0.00'
  }
  const { formatCurrency } = useFormatter()
  const formattedValue = formatCurrency(value, true, 2)
  const formattedPayPerHour = formatCurrency(getPayPerHour(), true, 2)
  return (
    <Horizontal justifyContent='flex-end' verticalCenter>
      <Texto weight='bold' className='pr-3'>
        {formattedValue}
      </Texto>
      {data.hours_worked > 0 && (
        <Texto appearance='hint' category='label' weight={600}>
          {`${formattedPayPerHour}/hr`}
        </Texto>
      )}
    </Horizontal>
  )
}
